import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { v4 as uuidv4 } from 'uuid'

import { Link } from '~/components'
import ResponsiveImage from '~/components/Image/ResponsiveImage'

import { getFileUrl } from '~/utils/cdnHelpers'

const mediaQuery = [{ screen: '(min-width: 280px)', width: '400px' }]

const bubbleImageMaskId = uuidv4()
const cubeFrontSurfaceSvgPath =
  'M33.0094 0.599609C35.674 0.599609 37.7471 2.71614 37.6386 5.32108C37.3455 12.311 37.0525 19.301 36.7594 26.2855C36.6509 28.8904 34.3987 31.007 31.734 31.007C22.9803 31.007 14.2266 31.007 5.4729 31.007C2.80826 31.007 0.735135 28.8904 0.843674 26.2855C1.13673 19.2955 1.42979 12.3056 1.72284 5.32108C1.83138 2.71614 4.08358 0.599609 6.74823 0.599609C15.5019 0.599609 24.2557 0.599609 33.0094 0.599609Z'

export default function TabGroupItem({
  href,
  isActive = false,
  label,
  filled = 0,
  classname,
  noBubbles = false,
  notClickable = false,
  roomColor,
  isLoading,
}: {
  href: string
  isActive?: boolean
  label: number
  filled?: number
  classname?: string
  noBubbles?: boolean
  notClickable?: boolean
  roomColor: string
  isLoading: boolean
}): React.ReactElement {
  const { t } = useTranslation(['mathVault'])
  const isUntouchedLevel = isActive || filled !== 0
  const style = isUntouchedLevel ? getTabStyle(roomColor, filled) : {}
  const inactiveTabClassnames =
    filled !== 0 ? getInactiveTabClassnames(roomColor) : roomColor === 'pink' ? 'text-hotpink' : 'text-purple'

  const bubbleImg = getBubbleImg(roomColor)

  const containerClasses = classNames('relative w-10', {
    classname,
    'loading-skeleton': isActive && isLoading,
  })

  const linkClasses = classNames('relative w-10 h-10 rounded flex items-center justify-center font-bold', {
    [inactiveTabClassnames]: !isActive,
    'loading-skeleton text-black': isLoading,
  })

  const frontPanelPositioning = 'absolute bottom-1 mb-0.5 mr-1 w-full'
  const bubbleImageClasses = classNames(frontPanelPositioning, 'object-cover w-full rounded')

  const levelColorClasses = classNames(frontPanelPositioning, 'box-border object-cover rounded')

  const borderPrimaryColor = getBorderPrimaryColor(roomColor)

  const borderClasses = classNames(frontPanelPositioning, 'box-border object-cover rounded', {
    [`border-dashed border-2 rounded-1.5 ${getActiveTabClassnames(roomColor)}`]: isActive,
  })

  const backBorderClasses = classNames(frontPanelPositioning, 'box-border object-cover rounded', {
    'border-dashed border-2 rounded-1.5': isActive,
  })

  const labelClasses = classNames('z-10 xl:text-md 2xl:text-xl pr-1 pb-1', {
    inactiveTabClassnames: !isActive,
    'text-white': !isLoading && isActive,
    'text-black': isLoading,
    'text-purple': roomColor === 'orange',
  })

  const backFillColor = getBackgroundFillColor(roomColor)

  return (
    <div className={containerClasses}>
      <ResponsiveImage
        alt={t('mathVault:iconAlts.level3d')}
        height={82}
        imageClassName="absolute bottom-0 object-cover w-full rounded scale-[1.2]"
        imageWidths="400"
        mediaQuery={mediaQuery}
        srcKey="Math Vault/icons/level-3d.png"
        width={90}
      />
      <Link
        className={`relative ${notClickable ? 'pointer-events-none' : ''}`}
        disabled={isActive}
        style={style}
        to={href}
      >
        <span className={linkClasses}>
          <svg
            className={levelColorClasses}
            height="32"
            viewBox="0 0 38 32"
            width="38"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d={cubeFrontSurfaceSvgPath} fill={isUntouchedLevel ? backFillColor : 'white'} />
          </svg>

          {!noBubbles && !isLoading ? (
            <svg
              className={bubbleImageClasses}
              preserveAspectRatio="none"
              style={{ height: `${filled * 0.8}%`, width: '37px' }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <mask id={bubbleImageMaskId}>
                <path d={cubeFrontSurfaceSvgPath} fill="#ffffff" />
              </mask>

              <image mask={`url(#${bubbleImageMaskId}})`} xlinkHref={bubbleImg} />
            </svg>
          ) : (
            ''
          )}

          <svg
            className={backBorderClasses}
            fillOpacity="0"
            height="32"
            style={{ borderStyle: 'solid', borderColor: borderPrimaryColor }}
            viewBox="0 0 38 32"
            width="38"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d={cubeFrontSurfaceSvgPath} />
          </svg>

          <svg
            className={borderClasses}
            fillOpacity="0"
            height="32"
            style={{ borderColor: roomColor === 'purple' ? 'purple' : roomColor === 'orange' ? 'orange' : 'white' }}
            viewBox="0 0 38 32"
            width="38"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d={cubeFrontSurfaceSvgPath} />
          </svg>

          <span className={labelClasses}>{label}</span>
        </span>
      </Link>
    </div>
  )
}

function getBorderPrimaryColor(roomColor: string): any {
  let color = null

  if (roomColor === 'purple') {
    color = '#FF9B05'
  } else if (roomColor === 'orange') {
    color = '#7D38D6'
  } else if (roomColor === 'pink') {
    color = '#DD2A88'
  }

  return color
}

function getBackgroundFillColor(roomColor: string): any {
  let color = null

  if (roomColor === 'purple') {
    color = '#DEBCFFFF'
  } else if (roomColor === 'orange') {
    color = 'rgb(255, 231, 196)'
  } else if (roomColor === 'pink') {
    color = '#EDA3CA'
  }

  return color
}

function getTabStyle(roomColor: string, filled: number): any {
  const style = {
    background: '',
  }

  if (roomColor === 'purple') {
    style.background = `linear-gradient(0, #7D38D6 ${filled}%, #DEBCFF 0%)`
  } else if (roomColor === 'orange') {
    style.background = `linear-gradient(0, #FF9B05 ${filled}%, #FFE7C4 0%)`
  } else if (roomColor === 'pink') {
    style.background = `linear-gradient(0, #DD2A88 ${filled}%, #EDA3CA 0%)`
  }

  return style
}

function getActiveTabClassnames(roomColor: string): string {
  switch (roomColor) {
    case 'purple':
      return 'text-white'
    case 'orange':
      return 'text-purple'
    case 'pink':
      return 'text-white'
    default:
      return ''
  }
}

function getInactiveTabClassnames(roomColor: string): string {
  switch (roomColor) {
    case 'purple':
      return 'text-white'
    case 'orange':
      return 'text-purple'
    case 'pink':
      return 'text-white'
    default:
      return ''
  }
}

function getBubbleImg(roomColor: string): string {
  switch (roomColor) {
    case 'purple':
      return getFileUrl('Math Vault/icons/bubbles/purple-bubbles.gif')
    case 'orange':
      return getFileUrl('Math Vault/icons/bubbles/orange-bubbles.gif')
    case 'pink':
      return getFileUrl('Math Vault/icons/bubbles/pink-bubbles.gif')
    default:
      return ''
  }
}
